<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { ScheduleBlockRulesRepository } from '../../modules/schedulerApi/schedule-block-rules/schedule-block-rules.repository'
    import { ScheduleBlockRulesService } from '../../modules/schedulerApi/schedule-block-rules/schedule-block-rules.service'
    import { ScheduleBlockRuleEntity } from '../../modules/schedulerApi/schedule-block-rules/schedule-block-rule.entity'
    import type { UpdateScheduleBlockRuleDto } from '../../modules/schedulerApi/schedule-block-rules/dto/update-schedule-block-rule.dto'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    const rep = new ScheduleBlockRulesRepository()
    const service = new ScheduleBlockRulesService(rep)

    export let scheduleBlockRuleId: string = null

    let entity: ScheduleBlockRuleEntity = new ScheduleBlockRuleEntity()
    let show = false

    let startTime: Date = null
    let endTime: Date = null

    let schedules: Schedule[] = []
    let scheduleOptions: { value: string; label: string }[] = []

    const weekdayOptions = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Segunda' },
        { value: 2, label: 'Terça' },
        { value: 3, label: 'Quarta' },
        { value: 4, label: 'Quinta' },
        { value: 5, label: 'Sexta' },
        { value: 6, label: 'Sábado' },
    ]

    async function loadSchedules() {
        schedules = await window.api.schedulers.schedules.getAll()
        schedules = schedules.filter((el) => el.status === 100)
        scheduleOptions = schedules.map((el) => ({ value: el.scheduleId, label: el.label }))
    }

    async function loadEntity() {
        try {
            show = true
            entity = await service.getOne(scheduleBlockRuleId)
            entity.startDate = new Date(entity.startDate)
            entity.endDate = entity.endDate ? new Date(entity.endDate) : null

            const base = new Date()
            startTime = new Date(base)
            startTime.setHours(0, entity.startMinute, 0, 0)
            endTime = new Date(base)
            endTime.setHours(0, entity.endMinute, 0, 0)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        // horário limpo devolve vazio (ver zinput-date-value) — sem isto, quebraria ao ler a hora abaixo
        if (!startTime || !endTime || isNaN(new Date(startTime).getTime()) || isNaN(new Date(endTime).getTime())) {
            evaluateError('Informe o horário de início e de término.')
            return
        }
        const dto: UpdateScheduleBlockRuleDto = {
            scheduleIds: entity.scheduleIds,
            weekdays: entity.weekdays,
            startMinute: startTime.getHours() * 60 + startTime.getMinutes(),
            endMinute: endTime.getHours() * 60 + endTime.getMinutes(),
            startDate: entity.startDate,
            endDate: entity.endDate || null,
            notes: entity.notes,
        }
        if (!validateDto(dto)) return
        try {
            show = true
            await service.update(scheduleBlockRuleId, dto)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto: UpdateScheduleBlockRuleDto) {
        if (!dto.scheduleIds || dto.scheduleIds.length === 0) {
            window.error('Selecione ao menos uma agenda.')
            return false
        }
        if (!dto.startDate) {
            window.error('Informe a data de início.')
            return false
        }
        if (dto.endDate && +new Date(dto.endDate) < +new Date(dto.startDate)) {
            window.error('A data final deve ser posterior à data de início.')
            return false
        }
        if (dto.endMinute <= dto.startMinute) {
            window.error('O horário de término deve ser depois do horário de início.')
            return false
        }
        return true
    }

    async function removeEntity() {
        if (
            !confirm(
                'Deseja mesmo excluir esta série de bloqueios recorrentes?\nAs ocorrências futuras serão canceladas. As ocorrências passadas permanecem no histórico.',
            )
        ) {
            return
        }
        try {
            show = true
            await service.remove(scheduleBlockRuleId)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function dispatchChange() {
        dispatch('change')
        dispatch('close')
    }

    onMount(async () => {
        await loadSchedules()
        await loadEntity()
    })
</script>

<LoadSpinner {show} />

<div class="root">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.scheduler.scheduler.write}
            <Zbutton color="red" icon={faTrashAlt} label="Excluir série" on:click={removeEntity}></Zbutton>
            <Zbutton color="primary" icon={faSave} label="Salvar" on:click={saveEntity}></Zbutton>
        {/if}
    </Zact>

    <Zframe class="m-3">
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput
                label="Agendas"
                size={12}
                sizeM={12}
                type="multiselect"
                options={scheduleOptions}
                bind:value={entity.scheduleIds}
                help="Selecione todas as agendas em que este bloqueio deve se repetir."
            />
            <Zinput
                label="Dias da semana"
                size={12}
                sizeM={12}
                type="multiselect"
                options={weekdayOptions}
                bind:value={entity.weekdays}
                help="Deixe todos marcados para repetir todos os dias."
            />
            <Zinput label="Início" size={3} sizeM={6} type="time" bind:value={startTime} />
            <Zinput label="Término" size={3} sizeM={6} type="time" bind:value={endTime} />
            <Zinput label="Data de início" size={3} sizeM={6} type="date" required={true} bind:value={entity.startDate} />
            <Zinput
                label="Data final (opcional)"
                size={3}
                sizeM={6}
                type="date"
                bind:value={entity.endDate}
                help="Deixe em branco para um bloqueio permanente."
            />
            <Zinput label="Motivo / anotações (opcional)" size={12} sizeM={12} type="text" bind:value={entity.notes} />
        </ZContainer>
    </Zframe>
</div>
